import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  map,
  Subscription,
  switchMap,
  timer,
  of,
  Observable,
} from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';

import { DateTimeService, ExtractContractService } from '@services/core';
import { environment } from 'src/environments/environment';
import {
  ContractObjectSchema,
  ContractorSchema,
  ContractSchema,
  ExtractContractSchema,
  UserSchema,
} from '@schemas/core';
import { BaseService } from '../base/base.service';
import { DEFAULT_DAYS_TO_ALERT_EXTRACT_CONTRACT_EXPIRATION } from '@shared/consts';
import { IExtractContractAlertDatatable } from '@shared/interfaces';

@Injectable({ providedIn: 'root' })
export class ExtractContractAlertService
  extends BaseService
  implements OnDestroy
{
  readonly env = environment;

  private hasExtractContractDocuments = new BehaviorSubject<boolean>(false);
  hasExtractContractDocuments$ =
    this.hasExtractContractDocuments.asObservable();

  subscriptions = new Subscription();

  constructor(
    private extractContractService: ExtractContractService,
    protected http: HttpClient,
    private dateTimeService: DateTimeService,
  ) {
    super(http);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  checkForExpiredExtractContactsDocuments(): void {
    const sub = timer(0, this.env.EXTRACT_CONTRACT_ALERT_TIME_CHECK)
      .pipe(
        switchMap(() =>
          this.extractContractService
            .findExtractContractsForSchoolWithExpirationDateCount()
            .pipe(
              map((x) => {
                const value = x > 0;
                console.log('Valor de School', x);
                this.hasExtractContractDocuments.next(value);
              }),
            ),
        ),
      )
      .subscribe();

    this.subscriptions.add(sub);
  }

  /**
   * La búsqueda por fechas se hace por la fecha de generación del FUEC
   * Se buscan los FUEC cuya fecha de generación esté dentro del rango
   * de fechas que se pasan por parámetro
   *
   * @param {IFilterByDateAndVehiclePlate} qParams
   * @param {(IStateDataTable | null)} [stateDataTable=null]
   * @return {*}  {Observable<ExtractContractSchema[]>}
   * @memberof ExtractContractService
   */
  findForExpiredExtractContract(params: {
    daysQty: number;
    expirationAlert: boolean;
  }): Observable<ExtractContractSchema[]> {
    let httpParams = new HttpParams()
      .set('_sort', 'correlative_field:DESC')
      .set(
        'end_date_field_gte',
        this.dateTimeService.getAPIDateFromDate(
          this.dateTimeService.getToday(),
        ),
      )
      .set(
        'end_date_field_lte',
        this.dateTimeService.getAPIDateFromDate(
          this.dateTimeService.getDateAddDays(
            this.dateTimeService.getToday(),
            params.daysQty || DEFAULT_DAYS_TO_ALERT_EXTRACT_CONTRACT_EXPIRATION,
          ),
        ),
      )
      .set('_limit', -1);

    if (params.expirationAlert) {
      httpParams = httpParams.append(
        'expiration_alert',
        params.expirationAlert,
      );
    }

    return this.http.get<ExtractContractSchema[]>(
      `${this.API_URL}/extract-contracts/alerts`,
      { params: httpParams },
    );
  }

  /**
   * Actualiza el campo expiration_alert de un extracto de contrato
   *
   * @param {string} id
   * @return {*}  {Observable<{ expiration_alert: true }>}
   * @memberof ExtractContractAlertService
   */
  updateExtractContractAlert(
    id: string,
  ): Observable<{ expiration_alert: true }> {
    return this.http.put<{ expiration_alert: true }>(
      `${this.API_URL}/extract-contracts/${id}/update-expiration-alert`,
      {},
    );
  }

  getTableHead(): Observable<string[]> {
    return of([
      'N°. FUEC',
      'N°. Contrato',
      'Contratante',
      'Objeto de Contrato',
      'Placa',
      'Fecha de inicio',
      'Fecha de fin',
      // 'Usuario que genera',
      // 'Fecha de generación',
    ]);
  }

  toDataTable(
    extractContract: ExtractContractSchema[],
  ): IExtractContractAlertDatatable[] {
    return extractContract.map((ec) => ({
      correlative: ec.extract_contract.correlative,
      contract_number: (ec.extract_contract?.contract as ContractSchema)
        ?.contract_number,
      contractor_name: (
        (ec.extract_contract?.contract as ContractSchema)
          ?.contractor as ContractorSchema
      )?.name,
      contract_object_description: (
        (ec.extract_contract?.contract as ContractSchema)
          .contract_object as ContractObjectSchema
      ).description,
      vehicle_plate: ec.vehicle_plate,
      extract_contract_start_date:
        this.dateTimeService.getDataTableCellDateFromAPIDate(
          ec.extract_contract.start_date,
        ),
      extract_contract_end_date:
        this.dateTimeService.getDataTableCellDateFromAPIDate(
          ec.extract_contract.end_date,
        ),
      expiration_alert: ec.expiration_alert,
      user_username: (ec.user as UserSchema)?.username,
      extract_contract_created_date:
        this.dateTimeService.getDataTableCellDateFromAPIDateTime(ec.created_at),
      id: ec.id,
    }));
  }
}
